import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Routes from 'routes';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { phonePropType } from 'config/propTypes';

import applicationSelector from 'store/selectors/application';
import {
	appReady as appReadyAction,
	componentRenderError as componentRenderErrorAction,
} from 'store/modules/app';
import { getTollfreePhoneNumber as getTollfreePhoneNumberAction } from 'store/modules/phone';

import { Header } from 'components/brand/Header';
import { Footer } from 'components/brand/Footer';
import { ExperimentModal } from 'components/experiment/ExperimentModal';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { TrackingPixel } from 'components/core/TrackingPixel';

import { Head } from 'components/core/Head';
import { OsanoScript } from 'components/core/OsanoScript';

const EMPTY = {};

class ErrorBoundary extends React.Component {
	state = { hasError: false };

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error, info) {
		this.props.onError(error, info.componentStack);
	}

	render() {
		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	onError: PropTypes.func.isRequired,
	children: PropTypes.node.isRequired,
};

export function ApplicationWrapper({
	errorMessage: errorMessageProp,
	phone: phoneProp,
	sftest: sftestProp,
	statusCode: statusCodeProp,
	actions: actionsProp,
	ready: readyProp,
}) {
	const dispatch = useDispatch();
	const location = useLocation();

	// Build a safe cookies-like object from document.cookie for the selector
	// The selector only uses cookies.get('sftest')
	const safeCookies = useMemo(
		() => ({
			get: (name) => {
				if (typeof document === 'undefined') return undefined;
				const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(
					document.cookie,
				);
				return match ? decodeURIComponent(match[1]) : undefined;
			},
		}),
		[],
	);

	const selectorProps = useMemo(
		() => ({ location, cookies: safeCookies }),
		[location, safeCookies],
	);
	const storeProps =
		useSelector((state) => applicationSelector(state, selectorProps)) ||
		EMPTY;

	const errorMessage = errorMessageProp ?? storeProps.errorMessage;
	const phone = phoneProp === undefined ? storeProps.phone : phoneProp;
	const sftest = sftestProp ?? storeProps.sftest;
	const statusCode = statusCodeProp ?? storeProps.statusCode;
	const ready = readyProp ?? storeProps.ready;

	const actions = useMemo(
		() =>
			actionsProp ?? {
				appReady: (...args) => dispatch(appReadyAction(...args)),
				componentRenderError: (...args) =>
					dispatch(componentRenderErrorAction(...args)),
				getTollfreePhoneNumber: (...args) =>
					dispatch(getTollfreePhoneNumberAction(...args)),
			},
		[actionsProp, dispatch],
	);

	useEffect(() => {
		actions.appReady();
	}, [actions]);

	return (
		<ErrorBoundary onError={actions.componentRenderError}>
			<div>
				{/* Render the default Head tags */}
				<Head />
				<OsanoScript />
				<Header
					phone={phone}
					getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
				/>
				<main>
					{statusCode === 200 && <Routes ready={ready} />}
					{statusCode !== 200 && (
						<ErrorPage
							statusCode={statusCode}
							message={errorMessage}
						/>
					)}
				</main>
				{sftest && <ExperimentModal />}
				<Footer
					phone={phone}
					getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
				/>
				<TrackingPixel src="/imageVerifyVisit" />
			</div>
		</ErrorBoundary>
	);
}

ApplicationWrapper.propTypes = {
	sftest: PropTypes.bool,
	statusCode: PropTypes.number,
	phone: phonePropType,
	errorMessage: PropTypes.string,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
		appReady: PropTypes.func.isRequired,
		componentRenderError: PropTypes.func.isRequired,
	}),
	ready: PropTypes.bool,
};

export default ApplicationWrapper;
